import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Home, AlertCircle } from 'lucide-react';
import axios from 'axios';

import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { VoucherDisplay } from '../components/voucher/VoucherDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function VoucherSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!sessionId) {
        setError(t.vouchers.errors.loading);
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/vouchers/session/${sessionId}`);
        if (cancelled) return;
        if (data.status === 'paid' && data.voucher) {
          setVoucher(data.voucher);
        } else {
          setError(t.vouchers.errors.loading);
        }
      } catch {
        if (!cancelled) setError(t.vouchers.errors.loading);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, t.vouchers.errors.loading]);

  return (
    <div className="pt-24 pb-20 bg-warm-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-accent mx-auto mb-4" />
            <p className="text-warm-700">{t.vouchers.success.sendingEmail}</p>
          </div>
        )}

        {!loading && error && (
          <div className="max-w-lg mx-auto text-center py-20">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-warm-700 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-accent hover:bg-accent-dark text-white rounded-full">
                <Home className="w-4 h-4 mr-2" />
                {t.vouchers.success.backHome}
              </Button>
            </Link>
          </div>
        )}

        {!loading && voucher && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <CheckCircle2 className="w-12 h-12 text-accent mx-auto mb-3" />
              <h1 className="font-heading text-3xl md:text-4xl text-warm-900 mb-2">
                {t.vouchers.success.title}
              </h1>
              <p className="text-warm-600">{t.vouchers.success.subtitle}</p>
            </motion.div>

            <VoucherDisplay voucher={voucher} />

            <div className="text-center mt-12">
              <Link to="/">
                <Button variant="outline" className="rounded-full">
                  <Home className="w-4 h-4 mr-2" />
                  {t.vouchers.success.backHome}
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
